import React, { useState, useEffect, useMemo, Fragment } from "react";
import { Plus } from "react-feather";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Typography,
} from "@mui/material";
import { DropdownItem } from "reactstrap";
import CustomSearchFilter from "../../Common/Component/CustomSearchFilter";
import ActionOptions from "../../Common/Component/ActionOptions";
import { showAlert } from "../../../Services/Shared.service";
import { getAirportsByCity } from "../../../Services/ServiceCity.service";
import TariffModal from "../../../CommonElements/TariffModal/TariffModal";
import AirportForm from "./AirportForm";
import { Spinner } from "../../../AbstractElements";

const AirportTab = ({ cityId }) => {
  const [airports, setAirports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchData, setSearchData] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);

  const fetchAirports = () => {
    setIsLoading(true);
    getAirportsByCity(cityId)
      .then((res) => {
        if (res?.data?.data) {
          setAirports(res.data.data);
        } else {
          setAirports([]);
        }
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.message || "Failed to fetch airport terminals";
        showAlert("error", errorMsg);
        setAirports([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSaveAirport = (savedAirport, isEdit) => {
    setAirports((prev) => {
      if (isEdit) {
        return prev.map((item) =>
          item.terminalId === savedAirport.terminalId ? savedAirport : item
        );
      } else {
        return [savedAirport, ...prev];
      }
    });
  };

  useEffect(() => {
    if (cityId) {
      fetchAirports();
    }
  }, [cityId]);

  const filteredData = useMemo(() => {
    if (!searchData) return airports;
    const query = searchData.toLowerCase();
    return airports.filter(
      (item) =>
        (item.terminalName && item.terminalName.toLowerCase().includes(query)) ||
        (item.terminalShortName && item.terminalShortName.toLowerCase().includes(query)) ||
        (item.specialReportingType && item.specialReportingType.toLowerCase().includes(query))
    );
  }, [airports, searchData]);

  useEffect(() => {
    setPage(0);
  }, [searchData]);


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddClick = () => {
    setEditingTerminal(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (row) => {
    setEditingTerminal(row);
    setIsModalOpen(true);
  };

  const paginatedData = useMemo(() => {
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  return (
    <Box sx={{ marginTop: "30px" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          gap: "15px",
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: "700", color: "#313131" }}>
          Airports
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: "15px" }}>
          <Box sx={{ minWidth: "250px" }}>
            <CustomSearchFilter
              searchData={searchData}
              setSearchData={setSearchData}
              placeholder="Search Airports"
            />
          </Box>
          <Box
            style={{
              color: "rgba(57, 181, 74, 1)",
              border: "1px solid rgba(57, 181, 74, 1)",
              borderRadius: "10px",
              backgroundColor: "rgba(225, 244, 228, 1)",
              height: "39px",
              minWidth: "140px",
              fontSize: "14px",
              padding: "0 20px",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              cursor: "pointer",
              fontWeight: "600",
              whiteSpace: "nowrap",
            }}
            onClick={handleAddClick}
          >
            <Plus size={18} />
            Add Airport
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <div className="loader-box" style={{ height: "20vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
          <Spinner attrSpinner={{ className: "loader-3" }} />
        </div>
      ) : (
        <>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              backgroundColor: "transparent",
              boxShadow: "0 4px 10px 1px rgba(8, 8, 8, 0.15)",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow
                  sx={{
                    "& th": {
                      color: "#626262",
                      fontWeight: "bold",
                      fontSize: "14px",
                      backgroundColor: "#E1F4E4",
                      borderBottom: "none",
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>Terminal Name</TableCell>
                  <TableCell>Short Name / Code</TableCell>
                  <TableCell>Parking Charge</TableCell>
                  <TableCell>Special Reporting Type</TableCell>
                  <TableCell align="center" sx={{ width: "80px" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      No Airports found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((row) => (
                    <TableRow
                      key={row.terminalId}
                      sx={{
                        "& td": {
                          borderBottom: "none",
                          fontSize: "14px",
                          color: "#313131",
                        },
                      }}
                    >
                      <TableCell style={{ fontWeight: "600" }}>{row.terminalName}</TableCell>
                      <TableCell>{row.terminalShortName || "N/A"}</TableCell>
                      <TableCell>₹{parseFloat(row.parkingCharge || 0).toFixed(2)}</TableCell>
                      <TableCell>{row.specialReportingType || "None"}</TableCell>
                      <TableCell align="center">
                        <ActionOptions key={row.terminalId}>
                          <Fragment>
                            <DropdownItem
                              className="border-0"
                              onClick={() => handleEditClick(row)}
                            >
                              <i className="fa fa-pencil" /> Edit
                            </DropdownItem>
                          </Fragment>
                        </ActionOptions>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              "& .MuiTablePagination-toolbar": {
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 2,
                textAlign: "center",
                overflowX: "hidden",
              },
              "& .MuiTablePagination-select": {
                backgroundColor: "rgba(255, 255, 255, 1)",
                borderRadius: "6px",
                border: "1px solid #D9D9D9",
              },
              "& .MuiTablePagination-actions button": {
                color: "rgba(112, 112, 112, 1)",
                transform: "scale(1.3)",
              },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                color: "rgba(98, 98, 98, 1)",
                fontSize: "14px",
                textAlign: "center",
                m: "auto",
                letterSpacing: "0.02vw",
              },
            }}
          />
        </>
      )}

      {/* Airport Terminal Form Modal */}
      <TariffModal
        isOpen={isModalOpen}
        size="lg"
        centered
        toggler={() => setIsModalOpen(false)}
        title={editingTerminal ? "Edit Airport Terminal" : "Add Airport Terminal"}
      >
        <AirportForm
          cityId={cityId}
          editingTerminal={editingTerminal}
          toggleModal={() => setIsModalOpen(false)}
          onAirportSaved={handleSaveAirport}
          fetchAirports={fetchAirports}
        />
      </TariffModal>
    </Box>
  );
};

export default AirportTab;
